/**
 * 三角形元素组件（SVG 渲染，支持全三边描边和阴影）。支持双击进入文字编辑模式。
 */
import { useState, useCallback } from 'react';
import type { TriangleElement as TriangleElementType } from '@/components/canvas/document/publicTypes';
import type { ElementComponentProps } from '@/components/canvas/elements/types';
import { appendBlurFilter, composeContainerTransform } from '../../group/containerTransform';
import { blurRadius, elementFillCss, firstShadow, firstStroke } from '@/components/canvas/elements/model';
import { useCanvas } from '@/components/canvas/designer/shell/CanvasContext';
import { ShapeTextEditor } from '../components/ShapeTextEditor';
import { resolveShapeTextStyle } from '../utils/shapeTextStyle';
import { createTextContentData } from '@/components/canvas/elements/text/factory';

/** 计算三角形顶点字符串，坐标在 width×height 空间内 */
function getPoints(direction: string, w: number, h: number): string {
  switch (direction) {
    case 'down':  return `0,0 ${w},0 ${w / 2},${h}`;
    case 'left':  return `${w},0 ${w},${h} 0,${h / 2}`;
    case 'right': return `0,0 ${w},${h / 2} 0,${h}`;
    default:      return `${w / 2},0 0,${h} ${w},${h}`;  // up
  }
}

/** stroke-dasharray 字符串 */
function getDashArray(style: string | undefined, lineWidth: number): string | undefined {
  if (style === 'dashed') return `${lineWidth * 4} ${lineWidth * 2}`;
  if (style === 'dotted') return `${lineWidth} ${lineWidth}`;
  return undefined;
}

export function TriangleElement({
  element,
  dragOffset = { dx: 0, dy: 0 },
  onSelect,
  onMouseDown,
}: ElementComponentProps<TriangleElementType>) {
  const { dx, dy } = dragOffset;
  const { updateElement, setEditingTableCellId } = useCanvas();
  const [isEditing, setIsEditing] = useState(false);

  const tri = element;
  const { width, height } = element.size;
  const stroke = firstStroke(tri.appearance);
  const strokeW = stroke?.width ?? 0;
  const shadowEffect = firstShadow(tri.appearance);
  const textStyle = resolveShapeTextStyle(element);

  const shadow = shadowEffect
    ? `drop-shadow(${shadowEffect.offsetX}px ${shadowEffect.offsetY}px ${shadowEffect.blur}px ${shadowEffect.color})`
    : undefined;

  const wrapperStyle: React.CSSProperties = {
    position: 'absolute',
    left: element.layout.x + dx,
    top: element.layout.y + dy,
    width,
    height,
    opacity: element.appearance.opacity,
    transform: composeContainerTransform(element),
    filter: appendBlurFilter(shadow, blurRadius(element)),
    cursor: 'inherit',
    overflow: 'visible',
  };

  const svgStyle: React.CSSProperties = {
    position: 'absolute',
    inset: 0,
    overflow: 'visible',
  };

  const handleDoubleClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setIsEditing(true);
  }, []);

  const handleEditStart = useCallback(() => {
    setEditingTableCellId(element.id);
  }, [element.id, setEditingTableCellId]);

  const handleEditEnd = useCallback(() => {
    setIsEditing(false);
    setEditingTableCellId(null);
  }, [setEditingTableCellId]);

  const handleCommit = useCallback((text: string) => {
    updateElement(element.id, {
      data: {
        ...element.data,
        text: text ? createTextContentData({ content: text }) : undefined,
      },
    });
  }, [element.id, element.data, updateElement]);

  return (
    <div
      style={wrapperStyle}
      data-element-hit
      data-element-id={element.id}
      onClick={(e) => {
        if (isEditing) return;
        e.stopPropagation();
        onSelect(e);
      }}
      onDoubleClick={handleDoubleClick}
      onMouseDown={(e) => {
        if (isEditing) return;
        onMouseDown?.(e, element);
      }}
    >
      <svg width={width} height={height} style={svgStyle}>
        <polygon
          points={getPoints(tri.data.direction ?? 'up', width, height)}
          fill={elementFillCss(tri) ?? '#ddd'}
          stroke={strokeW > 0 ? (stroke?.color ?? '#999') : 'none'}
          strokeWidth={strokeW}
          strokeDasharray={getDashArray(stroke?.style, strokeW) ?? undefined}
          strokeLinejoin="round"
        />
      </svg>
      <ShapeTextEditor
        text={element.data.text?.content ?? ''}
        textStyle={textStyle}
        isEditing={isEditing}
        onCommit={handleCommit}
        onEditStart={handleEditStart}
        onEditEnd={handleEditEnd}
      />
    </div>
  );
}
